// Heading detection: finds which way each render faces by matching its edges against the model's
// edges seen from the same point. Used by the dashboard when a points file has no headings.
import * as THREE from 'three';
import { VS_WORLD, VS_QUAD, FS_FEATURES, FS_CUBE_TO_EQUIRECT } from './shaders';
import type { TourEngine, EnginePano } from './engine';

export const EDGE_W = 512;
export const EDGE_H = 256;

/** Edge map of a render (Sobel on luminance, row-mean removed), at 512 × 256. Store it on pano.data.edge. */
export function imageEdges(src: CanvasImageSource): Float32Array {
  const W = EDGE_W, H = EDGE_H;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const c = cv.getContext('2d', { willReadFrequently: true })!;
  c.imageSmoothingQuality = 'high';
  c.drawImage(src, 0, 0, W, H);
  const d = c.getImageData(0, 0, W, H).data;
  const L = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) L[i] = (d[i * 4] * 0.3 + d[i * 4 + 1] * 0.59 + d[i * 4 + 2] * 0.11) / 255;
  const E = new Float32Array(W * H);
  const at = (x: number, y: number) => L[y * W + ((x + W) % W)];
  for (let y = 1; y < H - 1; y++) {
    let sum = 0;
    for (let x = 0; x < W; x++) {
      const gx = at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x - 1, y) - at(x - 1, y + 1);
      const gy = at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x, y - 1) - at(x + 1, y - 1);
      const m = Math.min(Math.hypot(gx, gy), 0.6);
      E[y * W + x] = m; sum += m;
    }
    const mean = sum / W;
    for (let x = 0; x < W; x++) E[y * W + x] -= mean;
  }
  return E;
}

interface SparseEdges { idx: Int32Array; val: Float32Array }

let featMat: THREE.ShaderMaterial | null = null;
let featCube: THREE.WebGLCubeRenderTarget | null = null;
let featRT: THREE.WebGLRenderTarget | null = null;
let eqScene: THREE.Scene | null = null;
let eqMat: THREE.ShaderMaterial | null = null;
const quadCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

/** The model's creases and depth breaks as seen from a pano, as a sparse equirect edge list. */
export function geometryEdges(engine: TourEngine, p: EnginePano): SparseEdges {
  const W = EDGE_W, H = EDGE_H, r = engine.renderer;
  if (!featMat) {
    featMat = new THREE.ShaderMaterial({ uniforms: { center: { value: new THREE.Vector3() } }, vertexShader: VS_WORLD, fragmentShader: FS_FEATURES, side: THREE.DoubleSide });
    featCube = new THREE.WebGLCubeRenderTarget(512, { generateMipmaps: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    featRT = new THREE.WebGLRenderTarget(W, H, { generateMipmaps: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    eqMat = new THREE.ShaderMaterial({ uniforms: { cube: { value: null } }, vertexShader: VS_QUAD, fragmentShader: FS_CUBE_TO_EQUIRECT, depthTest: false, depthWrite: false });
    const q = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), eqMat);
    q.frustumCulled = false;
    eqScene = new THREE.Scene();
    eqScene.add(q);
  }
  featMat.uniforms.center.value.copy(p.pos);
  const cc = engine.cubeCamera;
  engine.withOverride(featMat, () => {
    r.setClearColor(0x000000, 0);
    cc.renderTarget = featCube!;
    cc.position.copy(p.pos);
    cc.updateMatrixWorld(true);
    cc.update(r, engine.scene);
  });
  eqMat!.uniforms.cube.value = featCube!.texture;
  r.setRenderTarget(featRT);
  r.render(eqScene!, quadCam);
  r.setRenderTarget(null);
  const px = new Uint8Array(W * H * 4);
  r.readRenderTargetPixels(featRT!, 0, 0, W, H, px);
  const at = (x: number, y: number) => ((H - 1 - y) * W + ((x + W) % W)) * 4;
  const diff = (i: number, j: number) =>
    (Math.abs(px[i] - px[j]) + Math.abs(px[i + 1] - px[j + 1]) + Math.abs(px[i + 2] - px[j + 2])) / 255 + (4 * Math.abs(px[i + 3] - px[j + 3])) / 255;
  const idx: number[] = [], val: number[] = [];
  for (let y = 2; y < H - 2; y++) {
    const lat = (0.5 - (y + 0.5) / H) * Math.PI;
    if (Math.abs(lat) > 1.15) continue;
    const wl = Math.cos(lat);
    for (let x = 0; x < W; x++) {
      const i = at(x, y);
      const g = Math.max(diff(i, at(x + 1, y)), diff(i, at(x, y + 1)), diff(i, at(x - 1, y)), diff(i, at(x, y - 1)));
      if (g > 0.06) { idx.push(y * W + x); val.push(Math.min(1, g * 3) * wl); }
    }
  }
  return { idx: Int32Array.from(idx), val: Float32Array.from(val) };
}

function scoreCurve(G: SparseEdges, E: Float32Array, flip: boolean): Float32Array {
  const W = EDGE_W, sc = new Float32Array(W), n = G.idx.length;
  const xs = new Int32Array(n), rows = new Int32Array(n);
  for (let k = 0; k < n; k++) { rows[k] = (G.idx[k] / W) | 0; xs[k] = G.idx[k] - rows[k] * W; }
  for (let s = 0; s < W; s++) {
    let sum = 0;
    for (let k = 0; k < n; k++) {
      let xi = xs[k] - s; if (xi < 0) xi += W;
      if (flip) xi = W - 1 - xi;
      sum += G.val[k] * E[rows[k] * W + xi];
    }
    sc[s] = sum;
  }
  return sc;
}

function zscore(c: Float32Array): Float32Array {
  let m = 0; for (const v of c) m += v; m /= c.length;
  let sd = 0; for (const v of c) sd += (v - m) ** 2; sd = Math.sqrt(sd / c.length) || 1;
  return c.map((v) => (v - m) / sd);
}

function peak(c: Float32Array, lo = 0, hi = EDGE_W): { s: number; v: number } {
  const W = EDGE_W, wrap = (i: number) => ((i % W) + W) % W;
  let bi = lo;
  for (let s = lo; s < hi; s++) if (c[wrap(s)] > c[wrap(bi)]) bi = s;
  const b = wrap(bi), a = c[wrap(b - 1)], m = c[b], z = c[wrap(b + 1)];
  const den = a - 2 * m + z, off = den < 0 ? (0.5 * (a - z)) / den : 0;
  return { s: b + Math.max(-0.5, Math.min(0.5, off)), v: m };
}

const circ = (a: number, b: number) => { const d = Math.abs(a - b) % EDGE_W; return Math.min(d, EDGE_W - d); };

export interface HeadingResult {
  total: number;
  /** How many renders agree with the result on their own. */
  confident: number;
  /** True when the renders appear mirrored. */
  flip: boolean;
  /** True when the cameras share one heading and outliers were pulled to it. */
  shared: boolean;
}

/**
 * Sets `yaw` on each pano (needs `p.data.edge` from imageEdges and a model on the engine).
 * When most cameras agree on one heading (usual for a Max scene) every render uses it.
 */
export async function detectHeadings(
  engine: TourEngine,
  panos: EnginePano[],
  opts: { single?: boolean; flip?: boolean; onProgress?: (done: number, total: number) => void } = {},
): Promise<HeadingResult | null> {
  const list = panos.filter((p) => p.data.edge instanceof Float32Array);
  if (!list.length || !engine.meshes.length) return null;
  const res: Array<{ p: EnginePano; a: Float32Array; b: Float32Array }> = [];
  for (let i = 0; i < list.length; i++) {
    const G = geometryEdges(engine, list[i]);
    const E = list[i].data.edge as Float32Array;
    res.push({ p: list[i], a: zscore(scoreCurve(G, E, false)), b: zscore(scoreCurve(G, E, true)) });
    opts.onProgress?.(i + 1, list.length);
    if (i % 2 === 1) await new Promise((r) => requestAnimationFrame(() => r(null)));
  }
  let za = 0, zb = 0;
  for (const r of res) { za += peak(r.a).v; zb += peak(r.b).v; }
  const flip = opts.single ? !!opts.flip : zb > za * 1.15;
  const sum = new Float32Array(EDGE_W);
  for (const r of res) { const c = flip ? r.b : r.a; for (let s = 0; s < EDGE_W; s++) sum[s] += c[s]; }
  const g = peak(sum).s;
  const agree = res.filter((r) => circ(peak(flip ? r.b : r.a).s, g) <= 4).length;
  const shared = !opts.single && res.length >= 3 && agree >= res.length * 0.6;
  let confident = 0;
  for (const r of res) {
    const c = flip ? r.b : r.a, own = peak(c);
    const near = peak(c, Math.round(g) - 3, Math.round(g) + 4);
    let s: number;
    if (opts.single) s = own.s;
    else if (shared) s = near.s;
    else s = circ(own.s, g) <= 12 || own.v - near.v > 1.5 ? own.s : near.s;
    if (shared ? circ(own.s, g) <= 4 : own.v > 4) confident++;
    r.p.yaw = (s * 2 * Math.PI) / EDGE_W;
    // per-render flags for the dashboard's check step
    r.p.data.headingOk = shared ? circ(own.s, g) <= 4 : own.v > 4;
    r.p.data.headingOwn = (own.s * 360) / EDGE_W;
    r.p.data.headingShared = (g * 360) / EDGE_W;
  }
  return { total: res.length, confident, flip, shared };
}
